import { useMemo, useRef, useEffect, useState } from 'react';
import { LineChart } from '@mui/x-charts/LineChart';
import Card from '@mui/material/Card';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Site color palette
const CHART_COLORS = {
    primary: '#e91e63',
    secondary: '#7b809a',
    info: '#1A73E8',
    success: '#4CAF50',
    dark: '#344767',
    light: '#f0f2f5',
    grid: 'rgba(123, 128, 154, 0.2)', // Secondary color with opacity for grid
    text: '#344767', // Dark color for text
};

function MonthlyProjects({ projects = [] }) {
    // Aylık proje sayısı hesaplama
    let counterMonths = {};
    const months = [
        'Ocak',
        'Şubat',
        'Mart',
        'Nisan',
        'Mayıs',
        'Haziran',
        'Temmuz',
        'Ağustos',
        'Eylül',
        'Ekim',
        'Kasım',
        'Aralık',
    ];

    // Tüm ayları 0 değeriyle başlat
    months.forEach((month) => {
        counterMonths[month] = 0;
    });

    // Projeleri aylara göre grupla
    projects.forEach((project) => {
        if (project?.createdAt) {
            const d = new Date(project.createdAt);
            const monthName = months[d.getMonth()];
            counterMonths[monthName] += 1;
        }
    });

    // Chart verilerini oluştur
    const chartLabels = Object.keys(counterMonths);
    const chartData = Object.values(counterMonths);
    const hasData = projects.length > 0 && chartData.some((value) => value > 0);

    // Chart serisi
    const series = [
        {
            data: chartData,
            label: 'Aylara Göre Proje Sayısı',
            curve: 'linear',
            color: CHART_COLORS.info, // Primary color
        },
    ];

    // Responsive chart width için
    const chartContainerRef = useRef(null);
    const [chartWidth, setChartWidth] = useState(0);

    useEffect(() => {
        const updateChartWidth = () => {
            if (chartContainerRef.current) {
                setChartWidth(chartContainerRef.current.offsetWidth);
            }
        };

        // İlk render'da genişliği ayarla
        updateChartWidth();

        // ResizeObserver ile container boyutu değiştiğinde genişliği güncelle
        const resizeObserver = new ResizeObserver(updateChartWidth);
        if (chartContainerRef.current) {
            resizeObserver.observe(chartContainerRef.current);
        }

        // Cleanup
        return () => {
            if (chartContainerRef.current) {
                resizeObserver.unobserve(chartContainerRef.current);
            }
            resizeObserver.disconnect();
        };
    }, []);

    return (
        <Card sx={{ height: '100%' }}>
            {useMemo(
                () => (
                    <MDBox
                        ref={chartContainerRef}
                        variant="gradient"
                        bgColor="white"
                        borderRadius="xl"
                        coloredShadow="white"
                        height="12.5rem"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        {chartWidth > 0 && hasData ? (
                            <LineChart
                                series={series}
                                width={chartWidth}
                                xAxis={[
                                    {
                                        data: chartLabels,
                                        scaleType: 'point',
                                        tickLabelStyle: {
                                            color: CHART_COLORS.text,
                                            fontSize: 14,
                                            fontFamily: 'Roboto',
                                            fontWeight: 500,
                                        },
                                        gridStyle: {
                                            display: false,
                                        },
                                    },
                                ]}
                                yAxis={[
                                    {
                                        tickLabelStyle: {
                                            color: CHART_COLORS.text,
                                            fontSize: 14,
                                            fontFamily: 'Roboto',
                                        },
                                        gridStyle: {
                                            strokeDasharray: '5 5',
                                            stroke: CHART_COLORS.grid,
                                        },
                                    },
                                ]}
                                height={220}
                                margin={{ left: 5, right: 10, top: 10, bottom: 30 }}
                                slotProps={{
                                    legend: { hidden: true },
                                }}
                                sx={{
                                    '.MuiLineElement-root': {
                                        strokeWidth: 4,
                                        filter: 'drop-shadow(0px 2px 4px rgba(25, 0, 255, 0.3))', // Primary color shadow
                                    },
                                    '.MuiMarkElement-root': {
                                        stroke: '#fff',
                                        strokeWidth: 2,
                                        fill: CHART_COLORS.info,
                                        r: 6,
                                        ':hover': {
                                            r: 8,
                                            strokeWidth: 3,
                                            filter: 'brightness(1.1)',
                                        },
                                    },
                                }}
                            />
                        ) : (
                            <MDTypography color="text" variant="button">
                                Gösterilecek proje verisi bulunamadı
                            </MDTypography>
                        )}
                    </MDBox>
                ),
                [chartLabels, chartData, chartWidth, hasData]
            )}
        </Card>
    );
}

export default MonthlyProjects;
